import React, { useContext, useMemo } from 'react';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { Container } from '../../../../../../../../types/container';
import { CustomFieldContext } from '../../../../../../../../context/CustomFieldContext';
import { CustomField, CustomFieldEntityType, CustomFieldType } from '../../../../../../../../types/custom_field';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { ProductIdentifierUtil } from '../../../../../../../../util/product.util';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { TranslationKey } from '../../../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../../../types/translation/Translator';
import { UserContext } from '../../../../../../../../context/UserContext';

interface ProductTransactionIdentifier {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionIdentifier({
  transaction,
  setTransaction,
  productMasterData: pmd,
  disabled = false,
}: ProductTransactionIdentifier) {
  const { products } = useContext(ProductContext);
  const { customFields } = useContext(CustomFieldContext);
  const { locale } = useContext(UserContext);

  const selectableProducts = useMemo(() => {
    return [...(products.get(pmd?.id || '')?.values() || [])].filter(p => {
      if (p.stockLocationId !== transaction.product.fromStockLocationId) return false;
      if (transaction.product.fromBinId && p.binId !== transaction.product.fromBinId) return false;
      if (transaction.product.fromContainerId && p.containerId !== transaction.product.fromContainerId) return false;
      if (transaction.product.lotId && p.lotId !== transaction.product.lotId) return false;
      if (transaction.product.serialNbr && p.serial !== transaction.product.serialNbr) return false;
      if (transaction.product.lpn && p.lpn !== transaction.product.lpn) return false;
      if (transaction.product.kit?.fromKitId && p.kitId !== transaction.product.kit?.fromKitId) return false;
      return true;
    });
  }, [products, transaction]);

  const customFieldText = (field: CustomField, value?: string) => {
    if (!value) return '';

    if (field.type === CustomFieldType.date) {
      return new Date(value).toLocaleDateString(locale);
    }
    return value || '';
  };

  const handleIdentifierCustomField = (identifier?: string) => {
    const items = ProductIdentifierUtil.getAllIdentifierValues(identifier);
    const values = Array.from(items.keys());

    if (!identifier) {
      transaction.product.fromCustomFields = new Map();
      if (transaction.type === ProductTransactionType.move) {
        transaction.product.toCustomFields = new Map();
      }
      setTransaction(new ProductTransaction(transaction));
      return;
    }

    for (const key of values) {
      if (identifier) {
        const field = customFields.get(key);
        if (!field) continue;

        const value = items.get(key);
        if (!value) continue;

        setTransaction(transaction =>
          transaction.withFromCustomField({ ...field, entityType: CustomFieldEntityType.product, value }),
        );
        if (transaction.type === ProductTransactionType.move) {
          setTransaction(transaction =>
            transaction.withToCustomField({ ...field, entityType: CustomFieldEntityType.product, value }),
          );
        }
      }
    }
  };

  const identifierToText = (identifier: string) => {
    const values = ProductIdentifierUtil.getAllIdentifierValues(identifier);

    let txt = '';
    for (const [key, value] of values) {
      const field = customFields.get(key);
      if (!field) continue;
      if (field.hidden) continue;
      txt += `${customFieldText(field, value)} `;
    }
    return txt;
  };

  const toIdentifier = (transaction: ProductTransaction) => {
    const product = transaction.product;
    return ProductIdentifierUtil.createIdentifierString(
      new Map(Array.from(product.fromCustomFields.values()).map(cf => [cf.id, cf.value])),
    );
  };

  if (transaction.type === ProductTransactionType.inbound) return null;
  if (!transaction.product.fromCustomFields.size) return null;
  if (disabled) return null;

  const items = selectableProducts.filter(p => p.identifier).map(p => p.identifier);

  if (items.length === 0) return null;

  return (
    <Grid item xs={12}>
      <Grid container rowSpacing={1} columnSpacing={1}>
        <Grid item xs={6}>
          <DropdownSearchSelect
            mandatory
            disabled={disabled}
            label={t(TranslationKey.identifier).singular.label}
            placeholder={t(TranslationKey.identifier).singular.label}
            value={toIdentifier(transaction)}
            onSelect={item => {
              handleIdentifierCustomField(item);
            }}
            items={items.filter(p => p) as string[]}
            textBuilder={item => identifierToText(item)}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}
